import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, useTheme } from "@mui/material";

/**
 * Optimized Donut Chart Component
 * Relocated to components/chart
 */
const DonutChart = ({
    data,
    totalValue: propTotalValue,
    size = 200,
    strokeWidth = 20,
    animationDuration = 1,
    animationDelayPerSegment = 0.05,
    highlightOnHover = true,
    centerContent,
    onSegmentHover,
    ...props
}) => {
    const theme = useTheme();
    const [hoveredSegment, setHoveredSegment] = useState(null);

    const internalTotalValue = useMemo(
        () => propTotalValue || data.reduce((sum, segment) => sum + segment.value, 0),
        [data, propTotalValue]
    );

    const radius = size / 2 - strokeWidth / 2;
    const circumference = 2 * Math.PI * radius;
    let cumulativePercentage = 0;

    useEffect(() => {
        onSegmentHover?.(hoveredSegment);
    }, [hoveredSegment, onSegmentHover]);

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: size,
                height: size,
            }}
            onMouseLeave={() => setHoveredSegment(null)}
            {...props}
        >
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                style={{ overflow: 'visible', transform: 'rotate(-90deg)' }}
            >
                {/* Base background ring */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="transparent"
                    stroke={theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}
                    strokeWidth={strokeWidth}
                />

                {/* Data Segments */}
                <AnimatePresence>
                    {data.map((segment, index) => {
                        if (segment.value === 0) return null;

                        const percentage =
                            internalTotalValue === 0
                                ? 0
                                : (segment.value / internalTotalValue) * 100;

                        const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
                        const strokeDashoffset = (cumulativePercentage / 100) * circumference;

                        const isActive = hoveredSegment?.label === segment.label;

                        cumulativePercentage += percentage;

                        return (
                            <motion.circle
                                key={segment.label || index}
                                cx={size / 2}
                                cy={size / 2}
                                r={radius}
                                fill="transparent"
                                stroke={segment.color}
                                strokeWidth={strokeWidth}
                                strokeDasharray={strokeDasharray}
                                strokeDashoffset={-strokeDashoffset}
                                strokeLinecap="round"
                                initial={{ opacity: 0, strokeDashoffset: circumference }}
                                animate={{
                                    opacity: 1,
                                    strokeDashoffset: -strokeDashoffset,
                                }}
                                transition={{
                                    opacity: { duration: 0.3, delay: index * animationDelayPerSegment },
                                    strokeDashoffset: {
                                        duration: animationDuration,
                                        delay: index * animationDelayPerSegment,
                                        ease: "easeOut",
                                    },
                                }}
                                style={{
                                    cursor: highlightOnHover ? 'pointer' : 'default',
                                    filter: isActive
                                        ? `drop-shadow(0px 0px 8px ${segment.color}) brightness(1.2)`
                                        : 'none',
                                    transform: isActive ? 'scale(1.05)' : 'scale(1)',
                                    transformOrigin: 'center',
                                    transition: "filter 0.3s ease, transform 0.3s ease",
                                }}
                                onMouseEnter={() => setHoveredSegment(segment)}
                            />
                        );
                    })}
                </AnimatePresence>
            </svg>

            {/* Center Content */}
            {centerContent && (
                <Box
                    sx={{
                        position: 'absolute',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'auto',
                        width: size - strokeWidth * 2.5,
                        height: size - strokeWidth * 2.5,
                    }}
                >
                    {centerContent}
                </Box>
            )}
        </Box>
    );
};

export default DonutChart;
